import React from 'react';
import { flushSync } from 'react-dom';
import { createRoot } from 'react-dom/client';
import {
  IChatTranscript,
  ITranscriptFormatOptions,
} from 'src/app/chat/transcript';
import {
  buildTranscriptPrintStyles,
  ITranscriptPrintPalette,
  TranscriptPrintDocument,
} from 'src/app/chat/TranscriptPrintDocument';

export const TRANSCRIPT_PRINT_FRAME_ATTRIBUTE = 'data-chat-transcript-print';

let teardownActiveFrame: (() => void) | null = null;

export interface IPrintTranscriptOptions extends ITranscriptFormatOptions {
  palette: ITranscriptPrintPalette;
  documentTitle: string;
}

/**
 * @registry Prints a whole chat transcript from an off-screen iframe so the drawer's fixed/scrolling layout cannot clip it; the iframe's document title becomes the browser's default PDF file name.
 */
export const printTranscript = (
  transcript: IChatTranscript,
  { palette, documentTitle, ...formatOptions }: IPrintTranscriptOptions,
): void => {
  teardownActiveFrame?.();

  const frame = document.createElement('iframe');
  frame.setAttribute(TRANSCRIPT_PRINT_FRAME_ATTRIBUTE, '');
  frame.setAttribute('aria-hidden', 'true');
  frame.tabIndex = -1;
  // A display:none iframe has no layout and prints blank in some browsers, so
  // it stays rendered but collapsed and invisible.
  Object.assign(frame.style, {
    position: 'fixed',
    right: '0',
    bottom: '0',
    width: '0',
    height: '0',
    border: '0',
    visibility: 'hidden',
  });
  document.body.appendChild(frame);

  const frameWindow = frame.contentWindow;
  const frameDocument = frame.contentDocument;
  if (!frameWindow || !frameDocument) {
    frame.remove();
    throw new Error('The print view could not be created.');
  }

  // The initial about:blank document renders in quirks mode; rewriting it with
  // a doctype puts tables and fonts in standards mode.
  frameDocument.open();
  frameDocument.write('<!doctype html><html><head></head><body></body></html>');
  frameDocument.close();
  frameDocument.title = documentTitle;

  const meta = frameDocument.createElement('meta');
  meta.setAttribute('charset', 'utf-8');
  frameDocument.head.appendChild(meta);
  const style = frameDocument.createElement('style');
  style.textContent = buildTranscriptPrintStyles(palette);
  frameDocument.head.appendChild(style);

  const container = frameDocument.createElement('div');
  frameDocument.body.appendChild(container);
  const root = createRoot(container);
  flushSync(() => {
    root.render(
      <TranscriptPrintDocument
        transcript={transcript}
        palette={palette}
        options={formatOptions}
      />,
    );
  });

  // print() returns before the dialog closes in some browsers; tearing the
  // frame down any earlier would print an empty page.
  const teardown = () => {
    if (teardownActiveFrame === teardown) teardownActiveFrame = null;
    root.unmount();
    frame.remove();
  };
  teardownActiveFrame = teardown;
  frameWindow.addEventListener(
    'afterprint',
    () => {
      setTimeout(() => {
        if (teardownActiveFrame === teardown) teardown();
      }, 0);
    },
    { once: true },
  );

  frameWindow.focus();
  frameWindow.print();
};
